import type { ElectionContact, PastDeadlineElection, TableSort } from '@myrewards/contracts';
import { isApiError } from '../../api';
import {
  formatCount,
  formatDate,
  formatMoney,
  formatShortDate,
  formatTimestamp,
  plural,
  type ColumnSort,
  type ConfirmSummaryRow,
  type ListFilter,
} from '../../ui';
import { problemWords } from '../labels';
import { firstName } from '../letters/presenter';

// Elections past their deadline (SA4) in plain words: whose choice is late, how long since the
// letter and the deadline, every contact so far, and what chasing, extending or closing as all cash
// means for them. Missed elections are never paid as cash automatically; they wait here for a
// Stock Admin. One row per person (APP-067).

export type ElectionAction = 'chase' | 'extend' | 'close';

/** "14 days", "1 day", "Today". */
export function daysWords(days: number): string {
  return days === 0 ? 'Today' : plural(days, 'day', 'days');
}

/** The hero's sentence. */
export function pastDeadlineSentence(items: readonly PastDeadlineElection[]): string {
  if (items.length === 0)
    return 'Nobody is past their election deadline. If someone misses it, their election waits here until you chase, extend or close it.';
  const [only] = items;
  if (items.length === 1 && only !== undefined)
    return `${only.person.name} hasn’t chosen cash or stock by the deadline. Chase them, give them more time, or close the election as all cash.`;
  return `${formatCount(items.length)} people haven’t chosen cash or stock by their deadline. Chase them, give them more time, or close the election as all cash.`;
}

/** The person with the oldest letter, for the hero. */
export function oldestLetter(
  items: readonly PastDeadlineElection[],
): { item: PastDeadlineElection; date: string; days: number } | null {
  let best: { item: PastDeadlineElection; date: string; days: number } | null = null;
  for (const item of items) {
    if (item.letterDate === null || item.daysSinceLetter === null) continue;
    if (best === null || item.daysSinceLetter > best.days)
      best = { item, date: item.letterDate, days: item.daysSinceLetter };
  }
  return best;
}

/** The person open below: the one in the address, else the first on that award, else the first. */
export function selectedItem(
  items: readonly PastDeadlineElection[],
  lineId: string | null,
  awardId: string | null,
): PastDeadlineElection | null {
  return (
    items.find((i) => i.lineId === lineId) ??
    items.find((i) => i.award.id === awardId) ??
    items[0] ??
    null
  );
}

/** The action asked for in the address (`?action=`), if it's one of the three. */
export function actionFromParam(value: string | null): ElectionAction | null {
  return value === 'chase' || value === 'extend' || value === 'close' ? value : null;
}

/** Where the panel opens first: Chase when it can go today, else Extend. */
export function firstAction(item: PastDeadlineElection): ElectionAction {
  return item.actions.chase ? 'chase' : 'extend';
}

/** "Maritime HydroVision · manager Tessa Lindgren · letter dated Sep 30, 2026 · due Oct 7, 2026". */
export function personLine(item: PastDeadlineElection): string {
  const parts: string[] = [];
  if (item.sector !== null) parts.push(item.sector.name);
  else if (item.person.homeOrg !== null) parts.push(`home org ${item.person.homeOrg}`);
  parts.push(item.manager === null ? 'no manager on file' : `manager ${item.manager.name}`);
  if (item.letterDate !== null) parts.push(`letter dated ${formatDate(item.letterDate)}`);
  parts.push(`due ${formatDate(item.deadline.date)}`);
  return parts.join(' · ');
}

/** "EE# 10588 · Maritime HydroVision", for the table's employee cell. */
export function personSub(item: PastDeadlineElection): string {
  return [
    item.person.eeNumber === null ? null : `EE# ${item.person.eeNumber}`,
    item.sector?.name ?? item.person.homeOrg,
  ]
    .filter((v): v is string => v !== null)
    .join(' · ');
}

/** "7 days late", "1 day late". */
export function lateWords(daysLate: number): string {
  return daysLate === 0 ? 'Past its deadline' : `${plural(daysLate, 'day', 'days')} late`;
}

/** The reminders column: how many went, and when. */
export function remindersText(item: PastDeadlineElection): { count: string; dates: string | null } {
  const sent = item.contacts.filter((c) => c.kind === 'reminder');
  return {
    count: sent.length === 0 ? 'None sent' : `${formatCount(sent.length)} sent`,
    dates: sent.length === 0 ? null : sent.map((c) => formatShortDate(c.at)).join(' · '),
  };
}

/** The manager follow-up for this person, in words. */
export function nudgeWords(item: PastDeadlineElection): string {
  const n = item.managerNudge;
  if (n.sentAt !== null)
    return `Sent to ${n.to?.name ?? 'the manager'} · ${formatShortDate(n.sentAt)}`;
  if (!n.enabled) return 'Off';
  if (item.manager === null) return 'On · no manager on file';
  if (item.daysLate < n.daysLate)
    return `On · goes to ${item.manager.name} at ${plural(n.daysLate, 'day', 'days')} late`;
  return `On · due to ${item.manager.name}`;
}

/** When this person was chased today, and by whom; null when they weren't. */
export function chasedTodayWords(item: PastDeadlineElection): string | null {
  if (!item.chasedToday) return null;
  const today = [...item.contacts].reverse().find((c) => c.kind === 'chase');
  const by = today?.by?.name ?? 'Someone on the team';
  return `Chased today by ${by}${today === undefined ? '' : ` (${formatTimestamp(today.at)})`}. You can chase again tomorrow.`;
}

/**
 * What the chase email says, for the preview. The server's template (APP-067) writes the same
 * subject and first paragraph; keep the two in step.
 */
export function chaseEmail(item: PastDeadlineElection): {
  to: string;
  subject: string;
  body: string;
} {
  return {
    to: item.person.name,
    subject: 'Your award election is past its deadline',
    body: `The election for your Professional Achievement Award (${item.award.displayId}) was due ${formatDate(item.deadline.date)}. Your award is safe: nothing has been paid as cash. Open MyRewards to choose cash, stock or a mix for the ${formatMoney(item.cashCents)} cash portion.`,
  };
}

/** One line of the election's history: what happened, and the note or reason with it. */
export function contactWords(contact: ElectionContact): { title: string; note: string | null } {
  const to = contact.to?.name;
  switch (contact.kind) {
    case 'reminder':
      return { title: `Reminder emailed${to === undefined ? '' : ` to ${to}`}`, note: null };
    case 'chase':
      return {
        title: `Chased by ${contact.by?.name ?? 'the Stock Admin team'}${to === undefined ? '' : ` · emailed ${to}`}`,
        note: contact.note,
      };
    case 'extended':
      return {
        title: `Deadline extended by ${contact.by?.name ?? 'the Stock Admin team'}`,
        note: contact.note,
      };
    case 'manager_nudge':
      return { title: `Manager nudge emailed${to === undefined ? '' : ` to ${to}`}`, note: null };
  }
}

/** Why the new deadline can't be saved yet; null when it can. */
export function extendProblem(
  deadline: string,
  reason: string,
  asOf: string,
): { field: 'deadline' | 'reason'; message: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deadline))
    return { field: 'deadline', message: 'Choose the new deadline.' };
  if (deadline <= asOf) return { field: 'deadline', message: 'Choose a date after today.' };
  if (reason.trim() === '')
    return { field: 'reason', message: 'Give a reason: it’s logged on the award.' };
  return null;
}

/**
 * Closing as all cash, for the confirm summary: choice (a), the whole cash portion paid as cash
 * with tax withheld. The estimate is the one entered at the check; Payroll sets the final tax.
 */
export function closeSummary(item: PastDeadlineElection): {
  intro: string;
  rows: ConfirmSummaryRow[];
} {
  const first = firstName(item.person.name);
  const tax = item.estimateACents;
  const rows: ConfirmSummaryRow[] = [
    { label: `${first} gets`, value: `${formatMoney(item.cashCents)} as cash, with tax withheld` },
  ];
  if (tax !== null)
    rows.push(
      { label: 'Estimated tax', value: `${formatMoney(tax)} · Estimate` },
      {
        label: 'About this after tax',
        value: `${formatMoney(Math.max(0, item.cashCents - tax))} · Estimate`,
      },
    );
  if (item.options > 0)
    rows.push({
      label: 'Options',
      value: `${plural(item.options, 'option', 'options')}, not affected`,
    });
  rows.push(
    { label: 'Choice', value: '(a) All cash: no shares' },
    { label: 'Next', value: 'With Payroll, who set the final tax' },
  );
  return {
    intro: `${first} didn’t choose by the deadline. Closing it pays the whole cash portion as cash, the same as choosing (a). ${first} can’t choose stock after this.`,
    rows,
  };
}

/** Plain words for a refused chase, extension or close. */
export function electionProblemWords(error: unknown, name: string): string {
  const first = firstName(name);
  if (!isApiError(error)) return problemWords(error);
  const detail = error.problem?.detail;
  if (error.code === 'already_chased')
    return detail ?? `${first} was already chased today. You can chase again tomorrow.`;
  if (error.code === 'conflict' && detail === 'This election is no longer past its deadline.')
    return `${first}’s election isn’t past its deadline any more: they may have chosen, or a teammate gave more time. We’ve loaded the latest.`;
  if (error.code === 'conflict' && detail === 'This election is no longer available.')
    return `${first}’s election can’t be closed any more: they may have chosen, or a teammate closed it. We’ve loaded the latest.`;
  if (error.code === 'conflict')
    return `${first}’s award changed since this page loaded. We’ve loaded the latest; try again.`;
  if (error.code === 'forbidden')
    return 'Only the Stock Admin team can change an election past its deadline, and only for a PAA.';
  if (error.code === 'invalid_request')
    return detail ?? 'Choose a date after today and give a reason.';
  return problemWords(error);
}

// --- Sorting and filters (APP-149 D3-D5) ------------------------------------------------------

/** The list opens as the server sends it: the earliest deadline first, ties by award number. */
export const PAST_DEADLINE_DEFAULT_SORT: TableSort = { column: 'deadline', dir: 'asc' };
export const PAST_DEADLINE_DEFAULT_WORDS = 'earliest deadline first';
export const pastDeadlineTie = (item: PastDeadlineElection): string => item.award.displayId;

const reminders = (item: PastDeadlineElection): number =>
  item.contacts.filter((c) => c.kind === 'reminder').length;

/** How each column sorts, by column id. Dates compare as instants; a letter not out goes last. */
export const PAST_DEADLINE_SORTS: Record<
  'employee' | 'award' | 'letter' | 'deadline' | 'days' | 'reminders' | 'status',
  ColumnSort<PastDeadlineElection>
> = {
  employee: { value: (item) => item.person.name },
  award: { value: (item) => item.award.displayId },
  letter: {
    value: (item) => (item.letterDate === null ? null : Date.parse(item.letterDate)),
    words: ['oldest first', 'newest first'],
  },
  deadline: {
    value: (item) => Date.parse(item.deadline.at),
    words: ['earliest first', 'latest first'],
  },
  days: { value: (item) => item.daysSinceLetter },
  reminders: { value: reminders, words: ['fewest first', 'most first'] },
  status: {
    value: (item) => (item.chasedToday ? 1 : 0),
    words: ['past deadline first', 'chased today first'],
  },
};

export type PastDeadlineFilterId = 'status' | 'sector';

/**
 * The filters that mean something here (D5): whether someone was chased today, and their sector.
 * Everyone here is past their deadline on a PAA, so there's no stage or type to choose.
 */
export function pastDeadlineFilters(
  items: readonly PastDeadlineElection[],
): Record<PastDeadlineFilterId, ListFilter<PastDeadlineElection>> {
  const sectors = [
    ...new Set(items.flatMap((item) => (item.sector === null ? [] : [item.sector.name]))),
  ].sort((a, b) => a.localeCompare(b));
  return {
    status: {
      name: 'Status',
      options: [
        { value: 'any', label: 'Any status' },
        { value: 'past', label: 'Past deadline' },
        { value: 'chased', label: 'Chased today' },
      ],
      passes: (item, value) => item.chasedToday === (value === 'chased'),
      listAll: true,
    },
    sector: {
      name: 'Sector',
      options: [
        { value: 'all', label: 'All sectors' },
        ...sectors.map((name) => ({ value: name, label: name })),
      ],
      passes: (item, value) => item.sector?.name === value,
    },
  };
}

/** The params the list keeps in the address: its pager's, its sort's and its filters'. */
export const PAST_DEADLINE_PARAMS: readonly string[] = ['sort', 'dir', 'status', 'sector'];
